/**
 * DENET — kurallı popup
 *
 * Marka kuralları koda gömülüdür:
 * - Sayfa açılışında asla açılmaz.
 * - Kullanıcı en az N sayfa gezmeden gösterilmez.
 * - Kapatılırsa ayarlanan gün sayısı boyunca bir daha gösterilmez.
 * - Kaydolan kullanıcıya bir daha hiç gösterilmez.
 * - `prefers-reduced-motion` ve mobil tercihine uyar.
 * - Aynı anda başka bir katman açıksa (sepet, arama, menü) sıraya girmez, atlar.
 */

import { DenetElement, define, media } from '@denet/core';

const VIEWS_KEY = 'denet:pageviews';
const DAY = 86400000;

const readJSON = (key, fallback) => {
  try { return JSON.parse(window.localStorage.getItem(key) ?? '') ?? fallback; }
  catch { return fallback; }
};
const writeJSON = (key, value) => {
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* depolama kapalı */ }
};

/** Sayfa görüntüleme sayacı — tüm popup'lar aynı sayacı paylaşır. */
const bumpPageViews = () => {
  const n = Number(window.localStorage.getItem(VIEWS_KEY) ?? '0') + 1;
  try { window.localStorage.setItem(VIEWS_KEY, String(n)); } catch { /* yok say */ }
  return n;
};

const pageViews = bumpPageViews();

class DnPopup extends DenetElement {
  #shown = false;
  /** @type {number | undefined} */
  #timer;

  mounted() {
    this.dialog = /** @type {HTMLDialogElement | null} */ (this.ref('dialog'));
    if (!this.dialog) return;

    this.stateKey = `denet:popup:${this.dataset.id}`;
    const state = readJSON(this.stateKey, {});

    // Dönüşmüş kullanıcıya bir daha gösterilmez.
    if (state.converted) return;

    // Sıklık penceresi dolmadıysa gösterilmez.
    const days = Number(this.dataset.frequency) || 30;
    if (state.dismissedAt && Date.now() - state.dismissedAt < days * DAY) return;

    // Siteye yeni giren rahatsız edilmez.
    if (pageViews < (Number(this.dataset.minViews) || 1)) return;

    // Satın alma akışı bölünmez: ürün, sepet ve ödeme adımlarında popup yok.
    if (document.body.matches('.template-product, .template-cart')) return;

    // Mobilde kapalıysa hiç kurulmaz.
    const isMobile = !media.up('md');
    if (isMobile && this.dataset.mobile !== 'true') return;

    /* Çarpı yalnız #dismiss çağırıyordu: o sadece "bir daha gösterme" kaydını
       yazıyor, pencereyi kapatmıyor. Kapatma dialog'a bırakılıyor; kaydı da
       aşağıdaki 'close' dinleyicisi zaten tutuyor — Esc ve zemin tıklaması
       da aynı yoldan geçsin. */
    this.ref('close')?.addEventListener('click', () => this.dialog?.close(), { signal: this.signal });
    this.dialog.addEventListener('close', () => this.#dismiss(), { signal: this.signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog?.close();
    }, { signal: this.signal });

    this.ref('cta')?.addEventListener('click', () => this.#convert(), { signal: this.signal });
    this.querySelector('form')?.addEventListener('submit', () => this.#convert(), { signal: this.signal });

    // Shopify formu gönderdikten sonra sayfayı yeniden yükler; sonuç ekranı.
    if (new URLSearchParams(window.location.search).has('customer_posted')) {
      this.#convert();
      this.#open(true);
      return;
    }

    this.#arm(isMobile);
  }

  unmounted() { window.clearTimeout(this.#timer); }

  /** @param {boolean} isMobile */
  #arm(isMobile) {
    let trigger = this.dataset.trigger || 'exit';
    // Çıkış niyeti dokunmatik cihazda yoktur; kaydırmaya düşer.
    if (trigger === 'exit' && (isMobile || !media.pointerFine)) trigger = 'scroll';

    if (trigger === 'delay') {
      this.#timer = window.setTimeout(() => this.#open(), (Number(this.dataset.delay) || 20) * 1000);
      return;
    }

    if (trigger === 'scroll') {
      const target = (Number(this.dataset.scroll) || 70) / 100;
      const onScroll = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max > 0 && window.scrollY / max >= target) this.#open();
      };
      window.addEventListener('scroll', onScroll, { passive: true, signal: this.signal });
      return;
    }

    // Çıkış niyeti: imleç pencerenin üstünden çıkarken.
    document.addEventListener('mouseout', (event) => {
      if (event.relatedTarget || /** @type {MouseEvent} */ (event).clientY > 4) return;
      this.#open();
    }, { signal: this.signal });
  }

  /** @param {boolean} [force] */
  #open(force = false) {
    if (this.#shown || !this.dialog) return;
    // Başka bir katman açıksa araya girmez (sepet, arama, WhatsApp balonu).
    if (!force && document.querySelector('dialog[open], .dn-wa__bubble:not([hidden])')) return;

    this.#shown = true;
    window.clearTimeout(this.#timer);
    this.dialog.showModal();
  }

  #dismiss() {
    const state = readJSON(this.stateKey, {});
    if (state.converted) return;
    writeJSON(this.stateKey, { ...state, dismissedAt: Date.now() });
  }

  #convert() {
    writeJSON(this.stateKey, { converted: true, at: Date.now() });
    const body = this.ref('body');
    const done = this.ref('done');
    if (body && done) { body.hidden = true; done.hidden = false; }
  }
}

define('dn-popup', DnPopup);
